<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InsuranceDiscoveryList" label="Blanket VOB Search" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="box">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <div class="title-with-details">
                  <h1 class="title is-4">Blanket VOB Search</h1>
                  <a @click="showBlanketVOBModal = true" class="feature-details-link-title">
                    <span class="details-text">See details</span>
                    <b-icon icon="magnify-plus" size="is-small" class="bounce-icon"></b-icon>
                  </a>
                </div>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <b-field>
                  <b-select
                    v-model="selectedUserID"
                    placeholder="Show for User"
                    :loading="loading"
                    @input="handleSelect()"
                    class="user-select"
                  >
                    <option :value="0">All Users</option>
                    <option v-for="opt in userOptions" :value="opt.id" :key="opt.id">
                      {{ truncateName(opt.first_name, opt.last_name) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item">
                <b-field>
                  <b-select
                    v-model="selectedProviderID"
                    placeholder="Show for Provider"
                    :loading="loading"
                    @input="handleSelect()"
                    class="provider-select"
                  >
                    <option :value="0">All Providers</option>
                    <option v-for="opt in providerOptions" :value="opt.id" :key="opt.id">
                      {{ truncateText(opt.name, 25) }}
                    </option>
                  </b-select>
                </b-field>
              </div>
              <div class="level-item">
                <div class="subscription-wrapper">
                  <b-button
                    type="is-primary"
                    icon-left="plus"
                    :disabled="!canCreateBlanketVOB"
                    @click="
                      canCreateBlanketVOB
                        ? $router.push({ name: 'InsuranceDiscoveryCreate' })
                        : null
                    "
                  >
                    Create Blanket VOB Search
                  </b-button>

                  <div
                    v-if="!loading && user && !subscriptionNoticeClosed && blanketVOBNoticeMessage"
                    class="subscription-notice"
                  >
                    <button class="notice-close-btn" @click="closeSubscriptionNotice" type="button">
                      <b-icon icon="close" size="is-small"></b-icon>
                    </button>
                    <p class="has-text-danger has-text-weight-semibold mb-2">
                      {{ blanketVOBNoticeMessage }}
                    </p>
                    <div class="subscription-actions">
                      <b-button type="is-primary" size="is-small" @click="goToSubscriptionPage">
                        Enable Feature
                      </b-button>
                      <span class="mx-1">or</span>
                      <a
                        href="https://meetings.hubspot.com/bspircu/blanketvob"
                        target="_blank"
                        class="has-text-link is-size-7"
                      >
                        Talk to Sales
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />

          <div class="search-section">
            <b-field>
              <div class="field-body">
                <b-input
                  class="is-expanded"
                  :loading="loading"
                  v-model="search"
                  placeholder="Search by First or last name, Member ID, or Payer"
                  @input="handleSearch"
                />
                <b-dropdown
                  class="filter-dropdown"
                  position="is-bottom-left"
                  v-model="searchableFields"
                  multiple
                  aria-role="list"
                >
                  <template #trigger>
                    <b-button icon-right="filter" type="is-primary">
                      Search Fields ({{ searchableFields.length }})
                    </b-button>
                  </template>

                  <b-dropdown-item
                    v-for="field in allFields"
                    :key="field.value"
                    :value="field.value"
                    aria-role="listitem"
                  >
                    <div @click="toggleCheckbox(field.value)">
                      <b-checkbox
                        v-model="searchableFields"
                        :native-value="field.value"
                        class="checkbox-left"
                      >
                        {{ field.label }}
                      </b-checkbox>
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-field>
          </div>

          <div>
            <InsuranceDiscoveryTable
              :options="discoveryOptions"
              :current-page="currentPage"
              :page-size="pageSize"
              :total-items="totalItems"
              :loading="loading"
              @page-change="handlePageChange"
              @delete="handleDelete"
              @bulkremove="handleBulkRemove"
              @search-changed="handleSearchChange"
              @row-click="handleRowClick"
              @create-search="handleCreateSearch"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Blanket VOB Feature Detail Modal -->
    <b-modal v-model="showBlanketVOBModal" :width="640" scroll="keep">
      <BlanketVOBFeatureDetail></BlanketVOBFeatureDetail>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import InsuranceDiscoveryTable from '../components/InsuranceDiscoveryTable.vue';
import BlanketVOBFeatureDetail from '../components/BlanketVOBFeatureDetail.vue';
import request from '../request';
import { mapGetters } from 'vuex';

export default {
  name: 'InsuranceDiscoveryList',
  components: {
    InsuranceDiscoveryTable,
    BlanketVOBFeatureDetail,
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      selectedUserID: 0,
      selectedProviderID: 0,
      discoveryOptions: [],
      userOptions: [],
      providerOptions: [],
      loading: true,
      search: '',
      searchableFields: ['patient_first_name', 'patient_last_name', 'member_id', 'payer_name'],
      allFields: [
        { value: 'patient_first_name', label: 'First Name' },
        { value: 'patient_last_name', label: 'Last Name' },
        { value: 'member_id', label: 'Member ID' },
        { value: 'payer_name', label: 'Payer Name' },
      ],
      subscriptionNoticeClosed: false,
      subscription: null,
      showBlanketVOBModal: false,
    };
  },
  created() {
    axios
      .all([
        request('/users').then((resp) => {
          resp.data.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
          this.userOptions = resp.data.users;
        }),
        request('/providers').then((resp) => {
          resp.data.providers.sort((a, b) => a.name.localeCompare(b.name));
          this.providerOptions = resp.data.providers;
        }),
        request('/subscriptions/current').then((resp) => {
          this.subscription = resp.data.subscription;
        }),
        this.fetchDiscoveries(),
      ])
      .finally(() => (this.loading = false));
  },
  methods: {
    handleSelect() {
      this.loading = true;
      this.fetchDiscoveries().finally(() => (this.loading = false));
    },
    handleSearch() {
      this.currentPage = 1; // Reset to first page when searching
      this.fetchDiscoveries();
    },
    toggleCheckbox(value) {
      const index = this.searchableFields.indexOf(value);
      if (index === -1) {
        this.searchableFields.push(value); // Add the field to the selected list
      } else {
        this.searchableFields.splice(index, 1); // Remove the field from the selected list
      }
      this.handleSearch();
    },
    fetchDiscoveries() {
      this.loading = true;
      const args = {
        page: this.currentPage,
        limit: this.pageSize,
      };

      if (this.selectedUserID !== 0) {
        args.user_id = this.selectedUserID;
      }
      if (this.selectedProviderID !== 0) {
        args.provider_id = this.selectedProviderID;
      }

      // Add search parameters if search term exists
      if (this.search && this.search.trim()) {
        this.searchableFields.forEach((field) => {
          args[field] = this.search.trim();
        });
      }

      const params = new URLSearchParams(args).toString();
      return request(`/blanket-vob-search?${params}`)
        .then((resp) => {
          this.discoveryOptions = resp.data.list;
          // Get total count from header, fallback to list length if not available
          const totalCount = resp.headers['x-total-count'];
          if (totalCount) {
            this.totalItems = parseInt(totalCount, 10);
          } else {
            this.totalItems = resp.data.list.length;
          }
          this.loading = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            this.$buefy.toast.open({
              message: 'Session expired. Please log in again.',
              type: 'is-danger',
              duration: 5000,
            });
            this.$router.push({ name: 'login' });
          } else {
            this.$buefy.toast.open({
              message: 'An error occurred. Please try again later.',
              type: 'is-danger',
              duration: 5000,
            });
          }
          this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchDiscoveries();
    },
    handleSearchChange({ search, fields }) {
      this.search = search;
      this.searchableFields = fields;
      this.fetchDiscoveries();
    },
    handleDelete(id) {
      this.loading = true;
      request(`/blanket-vob-search/${id}`, {
        method: 'DELETE',
      })
        .then(() => {
          this.$buefy.toast.open({
            message: 'Blanket VOB Search deleted successfully',
            type: 'is-success',
            duration: 5000,
          });
          this.fetchDiscoveries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to delete Blanket VOB Search',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    handleRowClick(discovery) {
      if (discovery.insurance_discovery_payers && discovery.insurance_discovery_payers.length > 0) {
        // Route to the first payer response by default
        this.$router.push({
          name: 'InsuranceDiscoveryDetails',
          params: {
            id: discovery.id,
            payerResponseId: discovery.insurance_discovery_payers[0].id,
          },
        });
      } else {
        // If no payer responses, just route to the discovery details
        this.$router.push({
          name: 'InsuranceDiscoveryDetails',
          params: {
            id: discovery.id,
          },
        });
      }
    },
    handleBulkRemove(ids) {
      this.loading = true;
      request('/blanket-vob-search', {
        method: 'PUT',
        data: { insurance_discovery_ids: ids, is_hidden: true },
      })
        .then(() => {
          this.$buefy.toast.open({
            message: `Successfully removed ${ids.length} Blanket VOB Searches`,
            type: 'is-success',
            duration: 10000,
          });
          this.fetchDiscoveries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to remove Blanket VOB Searches',
            type: 'is-danger',
            duration: 5000,
          });
          this.loading = false;
        });
    },
    goToSubscriptionPage() {
      // Navigate to subscription/billing page with enable feature parameter
      this.$router
        .push({
          path: '/organization/settings/plan',
        })
        .then(() => {
          // Wait for the page to load, then scroll to the Blanket VOB Search section
          this.$nextTick(() => {
            setTimeout(() => {
              // Try to find the Blanket VOB Search section and scroll to it
              const blanketVobElement = document.querySelector(
                '[data-feature="blanket-vob"], [data-toggle="blanket-vob"], .blanket-vob-toggle, #blanket-vob-search'
              );
              if (blanketVobElement) {
                blanketVobElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              } else {
                // If specific element not found, scroll down to show toggles area
                window.scrollTo({
                  top: window.innerHeight,
                  behavior: 'smooth',
                });
              }
            }, 500); // Small delay to ensure page is fully loaded
          });
        })
        .catch(() => {
          // Fallback: use full URL if routing fails
          window.location.href = '/organization/settings/plan?enableFeature=blanket-vob';
        });
    },
    closeSubscriptionNotice() {
      this.subscriptionNoticeClosed = true;
    },
    handleCreateSearch() {
      // Check if user can create blanket VOB search
      if (this.canCreateBlanketVOB) {
        this.$router.push({ name: 'InsuranceDiscoveryCreate' });
      } else {
        // Show the subscription notice (make sure it's not closed)
        this.subscriptionNoticeClosed = false;
        // Scroll to the top to show the subscription notice
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },
    truncateName(firstName, lastName) {
      const fullName = `${firstName || ''} ${lastName || ''}`.trim();
      if (fullName.length <= 25) return fullName;

      // Try to fit first name + last initial
      if (firstName && lastName) {
        const truncated = `${firstName} ${lastName.charAt(0)}.`;
        if (truncated.length <= 25) return truncated;
      }

      // If still too long, truncate the first name
      if (firstName && lastName) {
        const maxFirstNameLength = 25 - lastName.length - 2; // -2 for " " and "."
        if (maxFirstNameLength > 0) {
          return `${firstName.substring(0, maxFirstNameLength)} ${lastName.charAt(0)}.`;
        }
      }

      // Fallback to simple truncation
      return this.truncateText(fullName, 25);
    },
  },
  computed: {
    ...mapGetters(['isManager', 'user']),
    selectedUser() {
      return this.userOptions.find((opt) => opt.id === this.selectedUserID);
    },
    blanketVOBSubscriptionProduct() {
      if (
        !this.subscription ||
        !this.subscription.subscription_products ||
        this.subscription.subscription_products.length === 0
      ) {
        return null;
      }
      // Find the first active blanket VOB subscription product
      const products = this.subscription.subscription_products.filter(
        (product) => product.product_id === this.subscription.blanket_vob_plan_id
      );
      if (products.length === 0) return null;
      // Try to find an active one
      const activeProduct = products.find((product) => product.is_active);
      if (activeProduct) return activeProduct;
      // If none are active, return the last one
      return products[products.length - 1];
    },
    blanketVOBRemainingInquiries() {
      const product = this.blanketVOBSubscriptionProduct;
      if (!product) return 0;
      return product.remaining_quota;
    },
    canCreateBlanketVOB() {
      // Don't make any decisions while data is still loading
      if (this.loading || !this.subscription || !this.user) {
        return false;
      }

      const product = this.blanketVOBSubscriptionProduct;

      // Can create if:
      // 1. User has active blanket VOB subscription (has_blanket_vob is true)
      // 2. OR has a blanket VOB product with remaining quota (even if cancelled)
      return (product && product.is_active) || (product && product.remaining_quota > 0);
    },
    blanketVOBNoticeMessage() {
      // Don't show any message while data is still loading
      if (this.loading || !this.subscription || !this.user) {
        return null;
      }

      const product = this.blanketVOBSubscriptionProduct;

      if (product && product.is_active) {
        if (product.plan_type === 'blanket_vob_trial') {
          return `Trial period. ${product.remaining_quota} remaining`;
        }
        return null;
      }

      if (product && product.remaining_quota > 0) {
        const statusText =
          product.status === 'cancelled' ? 'You cancelled this feature' : 'Feature not active';
        return `${statusText}. ${product.remaining_quota} remaining inquiries`;
      }

      return "You're not subscribed to this feature";
    },
  },
};
</script>

<style scoped>
.title {
  margin-bottom: 0 !important;
}

.title-with-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.level {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-right {
  flex-wrap: wrap;
  gap: 1rem;
}

.level-item {
  flex-shrink: 0;
}

.feature-details-link-title {
  color: #7c3aed;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  text-decoration: none;
}

.feature-details-link-title:hover {
  color: #6d28d9;
  text-decoration: underline;
}

.details-text {
  font-weight: 500;
}

.bounce-icon {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}
.b-field {
  margin-bottom: 0 !important;
}
.search-section {
  margin-bottom: 1rem;
}
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}

.subscription-wrapper {
  position: relative;
}

.subscription-notice {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fef7f7;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  padding: 0.75rem;
  font-size: 0.875rem;
  z-index: 10;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 250px;
}

.subscription-notice p {
  margin-bottom: 0.5rem;
}

.subscription-notice p:last-child {
  margin-bottom: 0;
}

.subscription-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mx-1 {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

.notice-close-btn {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
}

/* Select box text overflow handling */
.user-select .select select,
.provider-select .select select {
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.user-select .select select option,
.provider-select .select select option {
  max-width: 200px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0.5rem;
  line-height: 1.2;
}

/* For dropdown options, allow text to wrap */
.user-select .dropdown-content .dropdown-item,
.provider-select .dropdown-content .dropdown-item {
  white-space: normal;
  word-wrap: break-word;
  max-width: 200px;
  padding: 0.5rem;
  line-height: 1.2;
}
</style>
